/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { Image, IImageSettings, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';

import { IDlvryTileArticleBlockData } from './dlvry-tile-article-block.data';
import { IDlvryTileArticleBlockProps } from './dlvry-tile-article-block.props.autogenerated';
import { ensureBaseImageUrl } from '../../shared-utils/image-utilities';

export interface IDlvryTileArticleBlockViewProps extends IDlvryTileArticleBlockProps<IDlvryTileArticleBlockData> {
    TileArticleBlock: IModuleProps;
    linkProps: React.AnchorHTMLAttributes<HTMLAnchorElement>;
    image: JSX.Element | null;
    heading: JSX.Element | null;
    description: JSX.Element | null;
    cta: JSX.Element | null;
}

/**
 *
 * DlvryTileArticleBlock component
 * @extends {React.PureComponent<IDlvryTileArticleBlockProps<IDlvryTileArticleBlockData>>}
 */
class DlvryTileArticleBlock extends React.PureComponent<IDlvryTileArticleBlockProps<IDlvryTileArticleBlockData>> {

    private baseClassName = 'dlvry-tile-article-block';

    public render(): JSX.Element | null {
        const { config } = this.props;
        const viewProps: IDlvryTileArticleBlockViewProps = {
            ...this.props,
            TileArticleBlock: {
                className: this.baseClassName,
                moduleProps: this.props
            },
            linkProps: {
                className: `${this.baseClassName}__link`,
                href: config.link.linkUrl.destinationUrl,
                target: config.link.openInNewTab ? '_blank' : '_self',
                rel: config.link.openInNewTab ? 'noopener noreferrer' : undefined,
                "aria-label": config.link.ariaLabel,
                title: config.link.linkText
            },
            image: this.renderImage(),
            heading: this.renderHeading(),
            description: this.renderDescription(),
            cta: this.renderCta()
        };
        return this.props.renderView(viewProps);
    }

    private renderImage(): JSX.Element | null {
        const { context, config } = this.props;

        const imageSettings: IImageSettings = {
            viewports: {
                xs: { w: 520, h: 350, q: 'h=220&q=80&m=0&f=png' },
                sm: { w: 520, h: 350, q: 'h=220&q=80&m=0&f=png' },
                md: { w: 520, h: 350, q: 'h=220&q=80&m=0&f=png' },
                lg: { w: 520, h: 350, q: 'h=220&q=80&m=0&f=png' },
                xl: { w: 520, h: 350, q: 'h=220&q=80&m=0&f=png' }
            },
            lazyload: true,
            mode: 4
        };

        const placeholderImageName = ensureBaseImageUrl(context.request.app.config?.placeholderImageName);

        return (
            <Image
                className={`${this.baseClassName}__image`}
                requestContext={context.request}
                src={config.image?.src || placeholderImageName}
                gridSettings={context.request.gridSettings!}
                imageSettings={imageSettings}
                loadFailureBehavior='empty'
            />
        );
    }

    private renderHeading(): JSX.Element | null {
        const { context, config } = this.props;

        if (!config.heading) {
            return null;
        }

        return (
            <Text
                className={`${this.baseClassName}__heading`}
                tag={config.heading.tag || 'h2'}
                text={config.heading.text}
                editProps={{ onEdit: (event) => config.heading.text = event.target.value, requestContext: context.request }}
            />
        );
    }

    private renderDescription(): JSX.Element | null {
        const { context, config } = this.props;

        let descriptionText = config.description as string;

        // Check for <a...>...</a> or <a../> in the description, if it exists, remove it and just display the text.
        // Fixses issues with hydration since nested anchors inside another anchor are not valid HTML and cause issues in the editor.
        if (descriptionText && /<a.*?>|<\/a>/.test(descriptionText)) {
            descriptionText = descriptionText.replace(/<a.*?>|<\/a>/g, '');
        }

        return (
            <RichTextComponent
                className={`${this.baseClassName}__description`}
                text={descriptionText}
                editProps={{ onEdit: (event) => config.description = event.target.value, requestContext: context.request }}
            />
        );
    }

    private renderCta(): JSX.Element | null {
        const { resources } = this.props;

        return (
            <div className={`${this.baseClassName}__cta`}>
                <span className={`${this.baseClassName}__cta-link`}>
                    {resources.readMoreLabel}
                </span>
                <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2'>
                    <path d='M9 6l6 6-6 6'></path>
                </svg>
            </div>
        );
    }
}

export default DlvryTileArticleBlock;
